import { DocumentListSectionType, zDocumentListItem } from "#app/types/schema.ts";
import { SectionDefaultProps } from "#app/types/type.ts";
import { stegaClean } from "@sanity/client/stega";
import { memo, useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Link } from "react-router";
import { Icon } from "./ui/icon";
import { buttonVariants } from "./ui/button";
import { GDRLogo } from "./gdr-logo";
import { Separator } from "./ui/separator";
import { motion } from "motion/react";
import { cn } from "#app/utils/misc.tsx";
import SanityImage from "./sanity-image";
import { z } from "zod";
import { Heading } from "./text";
import { SERVICE_REGION } from "#app/utils/constants.ts";

type DocumentListSectionProps = SectionDefaultProps & {
    data: DocumentListSectionType;
};

export default function DocumentListSection({ data, encodeDataAttribute }: DocumentListSectionProps) {
    const documentListData = stegaClean(data);
    const { heading, headingLevel, documentType, documents, serviceAudienceFilter } = documentListData;
	const [hovered, setHovered] = useState<number | null>(null);

	return (
		<div>
			<div className='container'>
				{heading && (
                <Heading as={headingLevel} className='mx-auto text-h1 text-center'>
                    {heading}
                </Heading>
                )}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-16">
                    {documentType === 'service' && (
                        <Card className='bg-background min-h-96 md:min-h-[30rem] relative isolate overflow-hidden'>
                            <GDRLogo className='absolute bottom-0 right-0 text-muted -z-10' />
                            <CardHeader>
                                <CardTitle className='text-3xl lg:text-5xl'>Quality Garage Door Repair Services</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <CardDescription>We specialize in providing reliable, high-quality solutions for {serviceAudienceFilter} properties in {SERVICE_REGION} and surrounding areas. Contact us today to learn more about our services and how we can help you protect your {serviceAudienceFilter === "residential" ? "home" : "business"}.</CardDescription>
                            </CardContent>
                            <CardFooter>
                                <Link className={buttonVariants()} to="/get-a-quote">Get in touch <Icon name="messages-square" /></Link>
                            </CardFooter>
                        </Card>
                    )}
                    {documents?.map((document, index) => (
                        <DocumentCard
                            key={document.title}
                            document={document}
                            index={index}
                            hovered={hovered}
                            setHovered={setHovered}
                        />
                    ))}
                    {documentType === 'service' && serviceAudienceFilter === "residential" && (
                        <Card className='bg-accent text-accent-foreground min-h-96 md:min-h-[30rem] relative isolate overflow-hidden'>
                            <GDRLogo className='absolute bottom-0 right-0 text-muted-accent -z-10' />
                            <CardHeader>
                                <CardTitle className='text-3xl lg:text-5xl'>Get a free quote</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <CardDescription className='text-accent-foreground/80'>Contact us for personalized recommendations from our knowlegeable team</CardDescription>
                            </CardContent>
                            <CardFooter>
                                <Link className={buttonVariants()} to="/get-a-quote">Free Quote <Icon name="dollar-sign" /></Link>
                            </CardFooter>
                        </Card>
                    )}
				</div>
			</div>
		</div>
	)
}

const MotionCardDescription = motion.create(CardDescription);

// Define the type for the props using the Zod schema inference
type DocumentCardProps = {
    document: z.infer<typeof zDocumentListItem>;
    index: number;
    hovered: number | null;
    setHovered: React.Dispatch<React.SetStateAction<number | null>>;
};
  
export const DocumentCard = memo(
    ({ document, index, hovered, setHovered }: DocumentCardProps) => {
  
      // --- Determine Content Based on Document Type ---
      let displayDescription = "";
      let image = null; // Placeholder for potential background image URL if not using SanityImage directly
      let linkUrl = "#"; // Default/fallback link
  
      // Use document._type to access specific fields safely
      switch (document._type) {
        case 'service':
          displayDescription = document.shortDescription ?? ""; // Use nullish coalescing for safety
          // Assuming mainImage is processed correctly by SanityImage component
          // No need to extract URL here if SanityImage handles the object
          if (document.slug?.current) {
            linkUrl = `/services/${document.slug.current}`; // Adjust path as needed
          }
          image = document.mainImage; 
          break;
        case 'page':
          displayDescription = document.excerpt ?? "Read more"; // Provide fallback text
           if (document.slug?.current) {
            linkUrl = `/${document.slug.current}`; // Adjust path as needed
          }
          break;
        case 'jobListing':
          displayDescription = document.summary ?? "View details"; // Provide fallback text
           if (document.slug?.current) {
            linkUrl = `/careers/${document.slug.current}`; // Adjust path as needed
          }
          break; 
        case 'serviceArea':
            displayDescription = document.description ?? "Learn more"; // Provide fallback text
            if (document.slug?.current) {
                linkUrl = `/service-areas/${document.slug.current}`; // Adjust path as needed
            }
            break;     
      }
  
  
      return (
        // Wrap with Link for navigation
        <Card
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
            // Added group-hover for potential background scaling/effects
            className="bg-primary flex flex-col justify-end relative isolate overflow-hidden min-h-96 md:min-h-[30rem] transition-all duration-300 ease-out text-white group-hover:scale-105"
        >
            {/* Background Image - Conditional Rendering */}
            {image && (
                <SanityImage
                    image={image}
                    alt={document.title ?? ''} // Use title as alt fallback
                    className="absolute inset-0 w-full h-full object-cover -z-[10] transition-transform duration-300 ease-out"
                    sizes="(max-width: 1024px) 90vw, 30vw"
                    aria-hidden="true"
                />
            )}

            {/* Overlay */}
            <div className="absolute inset-0 bg-black opacity-50 -z-10" aria-hidden="true" />

            <CardHeader className='absolute top-0 w-full'>
                <div className='flex justify-end'>
                    <div className={cn(
                        buttonVariants({ variant: "white", size: "xl" }),
                        'rounded-full px-4 [&_svg]:size-6 transition-all duration-300 ease-out',
                        hovered === index ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                        )}>
                        <Icon name="arrow-top-right" />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {/* Use document.title - make sure title is nullable/optional in Zod if it can be missing */}
                <CardTitle className="text-2xl md:text-3xl">
                    <Link to={linkUrl} prefetch="intent">
                        <span className="absolute inset-0" />
                        {document.title ?? "Untitled Document"}
                    </Link>
                </CardTitle>
                <Separator className='h-0.5 bg-white mt-4' />
            </CardContent>
            <CardFooter>
                {/* Motion description - Animate based on hover state */}
                <MotionCardDescription layout
                    initial={false} // Prevent initial animation on load
                    animate={hovered === index ? { opacity: 1, height: 'auto', scaleY: 1, marginTop: '1rem', transition: { duration: 0.3, ease: 'easeOut' }} : { opacity: 0, height: 0, scaleY: 0, marginTop: '0rem', transition: { duration: 0.2, ease: 'easeIn' } }}
                    className={cn(
                    'text-gray-200 origin-top overflow-hidden' // Added overflow-hidden and origin-top
                    // Original classes:
                    // 'text-gray-200',
                    // hovered === index ? 'md:opacity-100 md:scale-y-100' : 'md:opacity-0 md:scale-y-0 md:h-0'
                    )}
                >
                    {/* Render the determined description */}
                    {displayDescription}
                </MotionCardDescription>
            </CardFooter>
        </Card>
      );
    }
);